// src/components/ProtectedRoute/Admin/Consoles/Pages/BionomicRecordsConsole.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import ConsoleTable from "../Framework/ConsoleTable";
import useConsoleData from "../Data/useConsoleData";
import bionomicRecordsPlugin from "../Plugins/bionomicRecords.plugin";
import { useToast } from "../Framework/ToastProvider";

import supabase from "../../../../../utils/supabase";

// ✅ 新しい統合エディタを使う
import BionomicRecordEditor from "../../Dialogs/BionomicRecordEditor";

const LOG_TAG = "[BionomicRecordsConsole]";

export default function BionomicRecordsConsole() {
  // DataGrid ベースの状態
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortModel, setSortModel] = useState([
    {
      field: bionomicRecordsPlugin.defaultSort.field,
      sort: bionomicRecordsPlugin.defaultSort.sort,
    },
  ]);
  const [search, setSearch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const toast = useToast();

  // ✅ 統合エディタの状態（追加= null, 編集= 選択行）
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorRow, setEditorRow] = useState(null);

  // Editor に渡す publications
  const [allPublications, setAllPublications] = useState([]);

  // 一覧の取得（useConsoleData）
  const { rows, total, loading, error } = useConsoleData({
    plugin: bionomicRecordsPlugin,
    page,
    pageSize,
    sortModel,
    search,
    refreshKey,
  });

  // テーブル列（プラグインから）
  const columns = useMemo(
    () =>
      bionomicRecordsPlugin.columns({
        onEdit: (row) => {
          console.debug(`${LOG_TAG} onEdit`, row?.id);
          setEditorRow(row);       // 編集対象をセット
          setEditorOpen(true);     // エディタを開く
        },
      }),
    []
  );

  // publications を取得（Editor で即使えるように）
  const fetchAllPublications = useCallback(async () => {
    console.debug(`${LOG_TAG} fetchAllPublications:start`);
    const { data, error } = await supabase
      .from("publications")
      .select("id, title_english")
      .order("id");
    if (error) {
      console.error(`${LOG_TAG} fetchAllPublications:error`, error);
      return;
    }
    console.debug(`${LOG_TAG} fetchAllPublications:ok`, data?.length || 0);
    setAllPublications(data || []);
  }, []);

  useEffect(() => {
    fetchAllPublications();
  }, [fetchAllPublications]);

  // 追加ボタン
  const handleOpenAdd = () => {
    setEditorRow(null);     // null なら「追加モード」
    setEditorOpen(true);
  };

  // Editor のクローズ
  const handleEditorClose = (saved) => {
    setEditorOpen(false);
    setEditorRow(null);
    if (saved) {
      console.debug(`${LOG_TAG} editor saved: refresh`);
      setRefreshKey((k) => k + 1);
      toast.push("success", "Bionomic record saved");
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          Bionomic Records
        </Typography>

        <TextField
          size="small"
          placeholder="Search by any field (id / taxa / country / remark …)"
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
        />

        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd}>
          Add
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || "Failed to load bionomic records"}
        </Alert>
      )}

      <ConsoleTable
        columns={columns}
        rows={rows}
        total={total}
        page={page}
        onPageChange={(p) => setPage(p)}
        pageSize={pageSize}
        onPageSizeChange={(s) => {
          setPage(0);
          setPageSize(s);
        }}
        sortModel={sortModel}
        onSortModelChange={(m) => {
          setPage(0);
          setSortModel(m);
        }}
        onRowClick={(row) => {
          console.debug(`${LOG_TAG} row click`, row?.id);
          setEditorRow(row);
          setEditorOpen(true);
        }}
        loading={loading}
      />

      {/* ✅ Add / Edit を 1 本化したエディタ */}
      {editorOpen && (
        <BionomicRecordEditor
          open={editorOpen}
          recordData={editorRow}             // null → 追加、オブジェクト → 編集
          allPublications={allPublications}  // 事前取得をそのまま供給
          onClose={handleEditorClose}
        />
      )}
    </Box>
  );
}
